/**
 * Admin Home Groups Page Config: edit page content (title, text, hero image).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { IconHomeHeart, IconAlertTriangle, IconPhoto } from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { useAuth } from "../../../lib/auth.tsx";
import { getAuthHeaders } from "../../../lib/graphql.ts";
import { getErrorMessage } from "../../../lib/graphql.ts";
import {
  graphql,
  withMinDelay,
  ADMIN_HOME_GROUPS_PAGE_CONFIG_QUERY,
  UPDATE_HOME_GROUPS_PAGE_CONFIG_MUTATION,
} from "../../../lib/graphql.ts";
import { config } from "../../../config.ts";
import { safeParseResponseJson } from "../../../lib/json-utils.ts";

interface PageConfigData {
  id: string;
  title: string | null;
  bodyText: string | null;
  imageUrl: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export function AdminHomeGroupsPageConfig() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { account } = useAuth();
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageCacheBust, setImageCacheBust] = useState(0);

  const [title, setTitle] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isActive, setIsActive] = useState(true);

  const loadData = useCallback(async () => {
    if (!account) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await withMinDelay(
        graphql<{ adminHomeGroupsPageConfig: PageConfigData | null }>(
          ADMIN_HOME_GROUPS_PAGE_CONFIG_QUERY,
        ),
      );
      const raw = data.adminHomeGroupsPageConfig;
      if (raw) {
        setTitle(raw.title ?? "");
        setBodyText(raw.bodyText ?? "");
        setImageUrl(raw.imageUrl ?? "");
        setIsActive(raw.isActive ?? true);
      } else {
        setTitle("Домашние группы");
        setBodyText("");
        setImageUrl("");
        setIsActive(true);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [account]);

  useEffect(() => {
    if (account) loadData();
  }, [account, loadData]);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/content");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  const handleSave = async () => {
    haptic("light");
    setIsSaving(true);
    setError(null);
    try {
      await graphql<{ updateHomeGroupsPageConfig: PageConfigData }>(
        UPDATE_HOME_GROUPS_PAGE_CONFIG_MUTATION,
        {
          input: {
            title: title.trim() || null,
            bodyText: bodyText.trim() || null,
            imageUrl: imageUrl.trim() || null,
            isActive,
          },
        },
      );
      await loadData();
      haptic("success");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    setIsUploadingImage(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(
        `${config.apiUrl}/upload/content/home-groups-page`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: formData,
        },
      );
      if (!response.ok) {
        const data = await safeParseResponseJson<{ error?: string }>(response, "page config image upload");
        throw new Error(data.error || "Ошибка загрузки");
      }
      const result = await response.json() as { url: string };
      setImageUrl(result.url);
      setImageCacheBust(Date.now());
      haptic("success");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const fieldClass =
    "w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500";
  const labelClass =
    "block text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-1";

  return (
    <Layout>
      <div className="pb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-600">
            <IconHomeHeart className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900">Домашние группы</h1>
            <p className="text-[13px] text-slate-500">
              Редактирование страницы
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 text-amber-600 text-sm">
            <IconAlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="card p-8 text-center text-slate-500 text-sm">
            Загрузка…
          </div>
        ) : (
          <div className="card overflow-hidden space-y-6 p-4">
            <div>
              <label className={labelClass}>Заголовок</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Домашние группы"
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Текст страницы</label>
              <textarea
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                rows={8}
                placeholder="Опишите домашние группы..."
                className={`${fieldClass} resize-none`}
              />
            </div>
            <div>
              <label className={labelClass}>Hero-картинка</label>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImageUpload(f);
                }}
              />
              {imageUrl && !isUploadingImage ? (
                <div className="relative rounded-xl overflow-hidden bg-slate-100">
                  <img
                    src={
                      imageUrl +
                      (imageUrl.includes("?") ? "&" : "?") +
                      `_t=${imageCacheBust}`
                    }
                    alt=""
                    className="w-full aspect-[2/1] object-cover"
                  />
                </div>
              ) : null}
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isUploadingImage}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 active:bg-slate-200 disabled:opacity-50"
                >
                  <IconPhoto className="w-4 h-4" />
                  {isUploadingImage ? "Загрузка…" : imageUrl ? "Заменить" : "Загрузить"}
                </button>
                {imageUrl && !isUploadingImage && (
                  <button
                    type="button"
                    onClick={() => setImageUrl("")}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 active:bg-amber-100"
                  >
                    Удалить
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-slate-300"
              />
              <label htmlFor="isActive" className="text-sm text-slate-700">
                Страница активна
              </label>
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary w-full"
            >
              {isSaving ? "Сохранение…" : "Сохранить"}
            </button>
          </div>
        )}

        <div className="h-6" />
      </div>
    </Layout>
  );
}
